/**
 * WHO sent a message (spec 2026-09-12-every-message-says-who-sent-it-design).
 *
 * A thread holds messages from several writers. Before this, only ONE of them was
 * recorded — a relayed lead↔teammate row carried `peer` — and every surface read the
 * absence of that stamp as "the person sent this". So the person became the default
 * owner of anything nobody bothered to sign, and Bash's own instructions to a teammate
 * rendered wearing the person's name (founder, 09-12: "I never sent those messages to
 * teammate, they are sent by bash, and it better be shown that way").
 *
 * Patching the surfaces was the wrong fix and was tried twice (#3583, #3586): a screen
 * cannot tell "the person sent this" from "nobody recorded who sent this", because both
 * look like nothing. The fix is to stop inferring. Every writer names itself at birth.
 *
 * This rides `content[0].sender`, the way `peer` already does — jsonb, no migration,
 * legacy readers ignore it. It is deliberately NOT folded into `peer`: `peer` is a
 * ROUTING record (which two tasks a relay joined, and why a report ended) and `sender`
 * is AUTHORSHIP. They overlap today by accident, and collapsing them would make the
 * routing record load-bearing for a label, which is how this drifted in the first place.
 */

import { LEAD_PEER_HANDLE } from './events.js';

export type MessageSender =
  /** The human. */
  | { kind: 'person' }
  /** The lead agent, in its own thread or writing into a lane it owns. */
  | { kind: 'bash' }
  /** A teammate or background helper, by the handle the person sees. */
  | { kind: 'agent'; handle: string }
  /** A platform notice. Never an actor, and never to be read as one. */
  | { kind: 'system' };

/**
 * The ONE derivation both relay sites share: the server's durable persist
 * (`onSubagentMessage`) and the web's optimistic twin (`teammateHandlers`). They used to
 * each answer separately, which is exactly how an optimistic row and the row that comes
 * back from a reload end up disagreeing about who spoke.
 *
 * `from` is the Phase 3 sender handle. The lead stamps its own reserved handle, and every
 * pre-Phase-3 row carries nothing at all — both read as Bash, because on that channel the
 * lead was the only writer before helpers could message each other.
 */
export function senderFromPeer(
  direction: 'to_teammate' | 'to_lead',
  name: string,
  from: string | undefined,
): MessageSender {
  // A report travelling UP was written by the teammate the row is attributed to. `from`
  // does not ride this leg, so `name` is the only answer there is, and it is the right one.
  if (direction === 'to_lead') return { kind: 'agent', handle: name };
  return from && from !== LEAD_PEER_HANDLE ? { kind: 'agent', handle: from } : { kind: 'bash' };
}

/**
 * Stamp a sender onto a block list, on the FIRST block — the same slot `peer` uses, so one
 * read answers for the message however many blocks it grew. An empty list is handed back
 * untouched: there is nothing to sign, and minting a block to carry a label would put an
 * empty bubble in a thread.
 */
export function withSender<T extends object>(blocks: T[], sender: MessageSender): T[] {
  return blocks.length === 0 ? blocks : [{ ...blocks[0], sender }, ...blocks.slice(1)];
}

/** Read the sender off a message's first content block, or null when nothing signed it. */
export function readSender(content: unknown): MessageSender | null {
  const first = Array.isArray(content) ? (content[0] as { sender?: unknown }) : undefined;
  const s = first?.sender as MessageSender | undefined;
  if (!s || typeof s !== 'object' || typeof (s as { kind?: unknown }).kind !== 'string') {
    return null;
  }
  switch (s.kind) {
    case 'person':
    case 'bash':
    case 'system':
      return { kind: s.kind };
    case 'agent':
      return typeof s.handle === 'string' && s.handle ? { kind: 'agent', handle: s.handle } : null;
    default:
      // A kind this build has not heard of. Unsigned is the honest reading — better than
      // rendering a label we cannot interpret, and it keeps the wire free to grow.
      return null;
  }
}

/**
 * The NAME a surface prints for a sender, or null for the person (whose own rows are
 * drawn with their avatar, not a name label).
 *
 * "Bash" rather than "Lead": it is what the rest of the product calls it — a lane's brief
 * reads "Bash asked", an ended lane reads "Stopped by Bash" — and "Lead" was internal
 * vocabulary on a surface built for non-technical people.
 */
export function senderLabel(sender: MessageSender): string | null {
  switch (sender.kind) {
    case 'person':
      return null;
    case 'bash':
      return 'Bash';
    case 'agent':
      return `@${sender.handle}`;
    case 'system':
      return 'Vestra';
  }
}
